import { HttpError, type GetListParams } from "react-admin";

import { createApiClient } from "@/api";
import { HTTP_STATUS } from "@/constants/http";
import { type DataProviderSchema } from "@/utils";
import { getLocalFilteredSortedList } from "@/utils/listParams";

const apiClient = createApiClient("jsonld");
const BASE_URL = "/api/vpn/radius_vpn_servers";

export const fetchRadiusVpnServersListData = async ({
  filter = {},
  pagination,
}: GetListParams) => {
  const typedFilter = filter as Record<string, unknown>;
  const response = await apiClient.GET(BASE_URL, {
    params: {
      query: {
        ...typedFilter,
        limit: pagination?.perPage,
        page: pagination?.page,
      },
    },
  });

  const data = response.data?.["hydra:member"] ?? [];
  const total = response.data?.["hydra:totalItems"] ?? 0;

  return {
    data,
    total,
  };
};

export const radiusVpnSchema: DataProviderSchema<"RadiusVpnServer", "jsonld"> =
  {
    list: getLocalFilteredSortedList(fetchRadiusVpnServersListData),
    detail: async ({ id }) => {
      const { data } = await apiClient.GET(`${BASE_URL}/{id}`, {
        params: {
          path: { id: String(id) },
        },
      });

      if (!data) throw new Error("User not found");

      return { data };
    },
    create: async ({ data }) => {
      const response = await apiClient.POST(BASE_URL, {
        body: data,
      });

      if (!response.data)
        throw new HttpError(
          "Something went wrong!",
          HTTP_STATUS.INTERNAL_SERVER_ERROR,
        );

      return {
        data: response.data,
      };
    },
    edit: async ({
      id,
      data: {
        ip,
        dataCenter,
        country,
        city,
        privacyType,
        accessLevel,
        active,
        host,
        protocolsCsv,
      },
    }) => {
      const response = await apiClient.PUT(`${BASE_URL}/{id}`, {
        params: {
          path: { id: String(id) },
        },
        body: {
          ip,
          dataCenter: dataCenter["@id"],
          country: country["@id"],
          city: city["@id"],
          privacyType,
          accessLevel: accessLevel["@id"],
          host,
          protocolsCsv,
          active,
        },
      });

      if (!response.data)
        throw new HttpError(
          "Something went wrong!",
          HTTP_STATUS.INTERNAL_SERVER_ERROR,
        );

      return {
        data: response.data,
      };
    },
    delete: (async (entity: { id: string }) =>
      await apiClient.DELETE(`${BASE_URL}/{id}`, {
        params: {
          path: { id: entity.id },
        },
      })) as unknown as NonNullable<
      DataProviderSchema<"RadiusVpnServer", "jsonld">["delete"]
    >,
  };
